import React, { useState, useEffect } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const images = [
  "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/10d78498-60e6-4cb8-8f48-f81fe0e15f7b/img-5827.jpeg",
  "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/10d78498-60e6-4cb8-8f48-f81fe0e15f7b/img-6524_1.jpeg",
  "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/10d78498-60e6-4cb8-8f48-f81fe0e15f7b/img-0030_1.jpeg",
  "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/10d78498-60e6-4cb8-8f48-f81fe0e15f7b/img-2015.jpeg"
];

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-[60vh] flex items-start pt-[88px] pb-8 overflow-hidden">
      {/* Background Slider */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence initial={false}>
          <motion.img
            key={currentIndex}
            src={images[currentIndex]}
            alt="Murerhåndværk i topkvalitet"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 2, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/40 to-transparent z-[1]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7 flex flex-col gap-6">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full bg-white border border-slate-200 border-t-4 border-t-orange-600 px-6 py-10 md:px-8 md:py-14 flex flex-col items-start relative overflow-hidden"
          >
            {/* Brick Watermark (Graphical) */}
            <div className="absolute -bottom-4 -right-4 opacity-15 pointer-events-none rotate-12" aria-hidden="true">
              <svg width="140" height="100" viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Row 1 */}
                <rect x="0" y="0" width="68" height="30" rx="1" fill="currentColor" className="text-orange-800" />
                <rect x="72" y="0" width="68" height="30" rx="1" fill="currentColor" className="text-orange-800" />
                {/* Row 2 */}
                <rect x="-36" y="34" width="68" height="30" rx="1" fill="currentColor" className="text-orange-800" />
                <rect x="36" y="34" width="68" height="30" rx="1" fill="currentColor" className="text-orange-800" />
                <rect x="108" y="34" width="68" height="30" rx="1" fill="currentColor" className="text-orange-800" />
                {/* Row 3 */}
                <rect x="0" y="68" width="68" height="30" rx="1" fill="currentColor" className="text-orange-800" />
                <rect x="72" y="68" width="68" height="30" rx="1" fill="currentColor" className="text-orange-800" />
              </svg>
            </div>
            
            <div className="inline-flex items-center mb-4 relative z-10">
              <span className="text-slate-900 text-xs font-bold uppercase tracking-wider">Flise- & murerforretning i Tønder</span>
            </div>
            
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight mb-3 relative z-10">
              Traditionelt håndværk <br />
              <span>med 25 års erfaring</span>
            </h1>
            
            <p className="text-sm md:text-base text-slate-700 mb-6 leading-relaxed max-w-lg relative z-10">
              SL. Flise- og murerforretning udfører alt inden for flise- og murerarbejde 
              samt reparationer. Vi bygger på murererfaring siden 2001 med fokus på kvalitet og holdbarhed.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-start relative z-10">
              <a
                href="#kontakt"
                className="inline-flex items-center justify-center bg-orange-600 text-white px-6 py-3 rounded-full font-bold text-base hover:bg-orange-700 transition-all group"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Slider Indicators */}
      <div className="absolute bottom-6 left-4 sm:left-6 lg:left-8 z-20 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              currentIndex === index ? 'bg-white w-8' : 'bg-white/40'
            }`}
            aria-label={`Gå til billede ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
